// FILE: src/pages/ReconciliationFormPage.jsx
//
// New/Edit Reconciliation — redesigned in the app's modern design language
// (scoped CSS under .rfpg-root, Geist typography, split layout with a live
// calculation rail, and a redesigned denomination counting grid).
// All behavior preserved from the previous version:
//   - GET /users/me?limit=1000 (currency), GET /accounts?limit=100000000
//   - GET /reconciliations/:id?limit=100000000 when editing
//   - GET /accounts/:id/balance?asOf=<date> auto-fetch while new or Draft
//   - actualCash = Σ(denomination × quantity); variance = actualCash − systemBalance
//   - POST /reconciliations/createDraft | PATCH /reconciliations/:id, then
//     alert + navigate("/reconciliations")
//   - Posted status = fully read-only, account locked when editing
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import API from "../api";
import { formatMoney } from "../utils/formatMoney";
import DenominationCounter from "../components/DenominationCounter";

const RECON_FORM_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* MUI papers reuse .rfpg-root for tokens — never inherit page sizing */
.MuiPaper-root.rfpg-root { min-height: 0; background: #fff; }
.rfpg-root {
  --rfpg-primary: #1E90FF;
  --rfpg-primary-soft: #E8F3FF;
  --rfpg-warn: #FFC107;
  --rfpg-warn-soft: #FFF7DC;
  --rfpg-warn-deep: #B86E00;
  --rfpg-success: #2E7D32;
  --rfpg-success-soft: #E8F5E9;
  --rfpg-danger: #C62828;
  --rfpg-danger-soft: #FDECEC;
  --rfpg-text-1: #111827;
  --rfpg-text-2: #374151;
  --rfpg-text-3: #6B7280;
  --rfpg-text-4: #9CA3AF;
  --rfpg-border-1: #ECECEC;
  --rfpg-border-2: #DDDDDD;
  --rfpg-surface: #FFFFFF;
  --rfpg-surface-sub: #FAFAFA;
  --rfpg-surface-sub-2: #F4F4F5;
  --rfpg-radius-sm: 6px;
  --rfpg-radius-md: 8px;
  --rfpg-radius-lg: 12px;
  --rfpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --rfpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--rfpg-text-1);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.rfpg-root * { box-sizing: border-box; }
.rfpg-root.rfpg-page {
  background: var(--rfpg-surface-sub);
  min-height: 100vh;
  padding: 0;
}

/* ----- Sticky top ----- */
.rfpg-sticky-top {
  position: sticky;
  top: var(--rfpg-navbar-h);
  z-index: 30;
  background: var(--rfpg-surface-sub);
  border-bottom: 1px solid var(--rfpg-border-1);
}
.rfpg-header {
  padding: 16px 24px 14px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.rfpg-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 0;
  color: var(--rfpg-text-3); font-family: inherit; font-size: 12px;
  cursor: pointer; margin-bottom: 6px;
}
.rfpg-back:hover { color: var(--rfpg-primary); }
.rfpg-title {
  font-size: 22px; font-weight: 600; letter-spacing: -0.02em;
  margin: 0; color: var(--rfpg-text-1);
  display: flex; align-items: center; gap: 10px;
}
.rfpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 9px 2px 7px;
  font-size: 11px; font-weight: 500; border-radius: 999px; white-space: nowrap;
}
.rfpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.rfpg-chip-draft { background: var(--rfpg-warn-soft); color: var(--rfpg-warn-deep); border: 1px solid #F1D88A; }
.rfpg-chip-draft .rfpg-chip-dot { background: var(--rfpg-warn-deep); }
.rfpg-chip-posted { background: var(--rfpg-success-soft); color: var(--rfpg-success); border: 1px solid #B7DBBA; }
.rfpg-chip-posted .rfpg-chip-dot { background: var(--rfpg-success); }
.rfpg-chip-reopened { background: var(--rfpg-primary-soft); color: #0E5EAB; border: 1px solid #BBDCFB; }
.rfpg-chip-reopened .rfpg-chip-dot { background: var(--rfpg-primary); }

/* ----- Body: form + rail ----- */
.rfpg-body {
  padding: 18px 24px 32px;
  display: grid; grid-template-columns: 1fr 300px; gap: 16px;
  max-width: 1100px; margin: 0 auto;
  align-items: start;
}
@media (max-width: 980px) { .rfpg-body { grid-template-columns: 1fr; } }

.rfpg-posted-banner {
  grid-column: 1 / -1;
  padding: 10px 14px;
  background: var(--rfpg-warn-soft);
  color: var(--rfpg-warn-deep);
  border: 1px solid #F1D88A;
  border-left: 3px solid var(--rfpg-warn);
  border-radius: var(--rfpg-radius-md);
  font-size: 13px;
}

.rfpg-card {
  background: var(--rfpg-surface);
  border: 1px solid var(--rfpg-border-1);
  border-radius: var(--rfpg-radius-lg);
  box-shadow: var(--rfpg-shadow-sm);
  padding: 16px 18px;
  margin-bottom: 14px;
}
.rfpg-card-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; font-weight: 600; color: var(--rfpg-text-1);
  margin-bottom: 12px;
}
.rfpg-card-head-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 4px;
  background: var(--rfpg-primary); color: #fff;
  font-size: 11px; font-weight: 700;
}
.rfpg-card-hint { margin-left: auto; font-size: 11px; font-weight: 400; color: var(--rfpg-text-3); }
@media (max-width: 640px) {
  .rfpg-header { padding: 12px 14px; }
  .rfpg-body { padding: 12px 12px 24px; gap: 12px; }
  .rfpg-card { padding: 12px; margin-bottom: 12px; }
}

.rfpg-frow-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 640px) { .rfpg-frow-2 { grid-template-columns: 1fr; } }
.rfpg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--rfpg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.rfpg-flab-req::after { content: ' *'; color: var(--rfpg-primary); font-weight: 700; }
.rfpg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--rfpg-border-2);
  border-radius: var(--rfpg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--rfpg-text-1); background: #fff;
}
.rfpg-finput:disabled { background: var(--rfpg-surface-sub); color: var(--rfpg-text-4); }
.rfpg-finput:focus {
  outline: none; border-color: var(--rfpg-primary);
  box-shadow: 0 0 0 3px var(--rfpg-primary-soft);
}
.rfpg-finput-mono { font-family: 'Geist Mono', monospace; font-size: 12.5px; }
.rfpg-ftextarea {
  width: 100%; min-height: 64px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--rfpg-border-2); border-radius: var(--rfpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--rfpg-text-1); background: #fff;
}
.rfpg-ftextarea:disabled { background: var(--rfpg-surface-sub); color: var(--rfpg-text-4); }
.rfpg-ftextarea:focus {
  outline: none; border-color: var(--rfpg-primary);
  box-shadow: 0 0 0 3px var(--rfpg-primary-soft);
}

/* MUI Autocomplete polish */
.rfpg-mui-ac .MuiOutlinedInput-root {
  font-family: 'Geist', sans-serif !important;
  font-size: 13px !important;
  padding: 2px 8px !important;
  min-height: 34px !important;
  background: #fff;
}
.rfpg-mui-ac .MuiOutlinedInput-notchedOutline {
  border-color: var(--rfpg-border-2) !important;
  border-radius: var(--rfpg-radius-md) !important;
}
.rfpg-mui-ac.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: var(--rfpg-primary) !important;
}

/* ----- Calculation rail ----- */
.rfpg-rail {
  position: sticky; top: calc(var(--rfpg-navbar-h) + 74px);
  display: flex; flex-direction: column; gap: 10px;
}
@media (max-width: 980px) { .rfpg-rail { position: static; } }
.rfpg-calc-row {
  display: flex; flex-direction: column; gap: 3px;
  padding: 10px 12px;
  background: var(--rfpg-surface);
  border: 1px solid var(--rfpg-border-1);
  border-radius: var(--rfpg-radius-md);
  box-shadow: var(--rfpg-shadow-sm);
}
.rfpg-calc-row.is-feature {
  background: linear-gradient(0deg, rgba(30,144,255,0.04), rgba(30,144,255,0.04)), #fff;
  border-color: rgba(30,144,255,0.15);
}
.rfpg-calc-lab {
  font-size: 10.5px; font-weight: 500;
  color: var(--rfpg-text-3); text-transform: uppercase; letter-spacing: 0.04em;
}
.rfpg-calc-val {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 17px; font-weight: 600;
  color: var(--rfpg-text-1);
}
.rfpg-calc-val.is-pos { color: var(--rfpg-success); }
.rfpg-calc-val.is-neg { color: var(--rfpg-danger); }
.rfpg-calc-formula {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; color: var(--rfpg-text-4); margin-top: 1px;
}
.rfpg-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  height: 36px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--rfpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease; white-space: nowrap;
}
.rfpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rfpg-btn-primary { background: var(--rfpg-primary); color: #fff; width: 100%; }
.rfpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }

.rfpg-state { padding: 64px 32px; text-align: center; color: var(--rfpg-text-3); }
.rfpg-state-title { color: var(--rfpg-text-2); font-size: 14px; font-weight: 500; margin-top: 12px; }
`;

function IconBack({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatusChip({ status }) {
  if (status === "Posted") {
    return (
      <span className="rfpg-chip rfpg-chip-posted">
        <span className="rfpg-chip-dot" /> Posted
      </span>
    );
  }
  if (status === "Reopened") {
    return (
      <span className="rfpg-chip rfpg-chip-reopened">
        <span className="rfpg-chip-dot" /> Reopened
      </span>
    );
  }
  return (
    <span className="rfpg-chip rfpg-chip-draft">
      <span className="rfpg-chip-dot" /> Draft
    </span>
  );
}

function ReconciliationFormPage() {
  const { id } = useParams(); // If present, we are editing an existing record
  const navigate = useNavigate();

  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState("");
  const [systemBalance, setSystemBalance] = useState(0);
  const [actualCash, setActualCash] = useState(0);
  const [variance, setVariance] = useState(0);
  const [note, setNote] = useState("");
  const [denominations, setDenominations] = useState([
    { denomination: 100, quantity: 0 },
    { denomination: 50, quantity: 0 },
    { denomination: 20, quantity: 0 },
    { denomination: 10, quantity: 0 },
    { denomination: 5, quantity: 0 },
    { denomination: 2, quantity: 0 },
    { denomination: 1, quantity: 0 },
    { denomination: 0.50, quantity: 0 },
    { denomination: 0.20, quantity: 0 },
    { denomination: 0.10, quantity: 0 },
    { denomination: 0.05, quantity: 0 },
    { denomination: 0.02, quantity: 0 },
    { denomination: 0.01, quantity: 0 },
  ]);

  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState("Draft");
  const [loading, setLoading] = useState(false);

  // Currency state initialized as USD by default, fetched from user preferences
  const [currency, setCurrency] = useState("USD");

  const isPosted = status === "Posted";

  // Fetch user currency preference
  useEffect(() => {
    async function fetchUserCurrency() {
      try {
        const res = await API.get("/users/me?limit=1000");
        setCurrency(res.data.currency || "USD");
      } catch (error) {
        console.error("Error fetching user currency:", error);
      }
    }
    fetchUserCurrency();
  }, []);

  // Load accounts + possibly existing reconciliation
  useEffect(() => {
    loadAccounts();
    if (id) {
      loadReconciliation(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Recompute variance automatically whenever actualCash or systemBalance changes
  useEffect(() => {
    setVariance(actualCash - systemBalance);
  }, [actualCash, systemBalance]);

  // Auto-fetch system balance (only if new or still "Draft")
  useEffect(() => {
    if ((!id || status === "Draft") && accountId && date) {
      fetchSystemBalance(accountId, date);
    }
  }, [accountId, date, id, status]);

  // 1) Load accounts
  async function loadAccounts() {
    try {
      const res = await API.get("/accounts?limit=100000000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Error loading accounts:", err);
    }
  }

  // 2) Load existing reconciliation if editing
  async function loadReconciliation(recId) {
    setLoading(true);
    try {
      const res = await API.get(`/reconciliations/${recId}?limit=100000000`);
      const { reconciliation, denominations: denoms } = res.data;
      if (reconciliation) {
        setAccountId(reconciliation.account?._id || "");
        setDate(reconciliation.date.split("T")[0]);
        setSystemBalance(reconciliation.systemBalance);
        setActualCash(reconciliation.actualCash);
        setNote(reconciliation.note || "");
        setStatus(reconciliation.status || "Draft");

        if (denoms && denoms.length > 0) {
          setDenominations(
            denoms.map((d) => ({
              denomination: d.denomination,
              quantity: d.quantity,
            }))
          );
        }
      }
    } catch (err) {
      console.error("loadReconciliation Error:", err);
      alert("Failed to load reconciliation.");
    } finally {
      setLoading(false);
    }
  }

  // 3) Fetch system balance from server
  async function fetchSystemBalance(accId, dateStr) {
    try {
      const res = await API.get(`/accounts/${accId}/balance`, {
        params: { asOf: dateStr },
      });
      setSystemBalance(res.data.balance || 0);
    } catch (err) {
      console.error("fetchSystemBalance Error:", err);
      // We can leave systemBalance at 0 or show an error.
    }
  }

  // Denominations: handle change and recompute actual cash
  function handleDenominationChange(index, value) {
    const newDenoms = [...denominations];
    newDenoms[index].quantity = Number(value);
    setDenominations(newDenoms);

    // Recompute actualCash from all denominations
    const sum = newDenoms.reduce(
      (acc, d) => acc + d.denomination * d.quantity,
      0
    );
    setActualCash(sum);
  }

  // 4) Handle Save (Create or Update)
  async function handleSave() {
    try {
      const payload = {
        accountId,
        date,
        systemBalance,
        actualCash,
        note,
        denominations,
      };

      if (!id) {
        // CREATE a new draft
        const res = await API.post("/reconciliations/createDraft", payload);
        alert(res.data.message);
      } else {
        // UPDATE an existing draft
        const res = await API.patch(`/reconciliations/${id}`, payload);
        alert(res.data.message);
      }
      navigate("/reconciliations");
    } catch (err) {
      console.error("handleSave Error:", err);
      alert("Failed to save draft.");
    }
  }

  const varianceCls =
    Math.abs(variance) < 0.005 ? "" : variance > 0 ? "is-pos" : "is-neg";

  // Render
  return (
    <div className="rfpg-root rfpg-page">
      <style>{RECON_FORM_CSS}</style>

      {/* Sticky top */}
      <div className="rfpg-sticky-top">
        <div className="rfpg-header">
          <div>
            <button
              type="button"
              className="rfpg-back"
              onClick={() => navigate("/reconciliations")}
            >
              <IconBack /> Back to Reconciliations
            </button>
            <h1 className="rfpg-title">
              {id ? "Edit Reconciliation" : "New Reconciliation"}
              {id && <StatusChip status={status} />}
            </h1>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rfpg-state">
          <CircularProgress size={28} />
          <div className="rfpg-state-title">Loading reconciliation…</div>
        </div>
      ) : (
        <div className="rfpg-body">
          {isPosted && (
            <div className="rfpg-posted-banner" role="status">
              This reconciliation is already posted; editing is read-only.
            </div>
          )}

          {/* Left column: form */}
          <div>
            {/* Account + Date */}
            <section className="rfpg-card">
              <div className="rfpg-frow-2">
                <div>
                  <label className="rfpg-flab rfpg-flab-req">Account</label>
                  <Autocomplete
                    className="rfpg-mui-ac"
                    size="small"
                    options={accounts
                      .slice()
                      .sort((a, b) =>
                        a.accountName.localeCompare(b.accountName)
                      )}
                    getOptionLabel={(option) =>
                      `${option.accountName} (${option.accountNo})`
                    }
                    onChange={(event, newValue) => {
                      setAccountId(newValue ? newValue._id : "");
                    }}
                    value={
                      accounts.find((acc) => acc._id === accountId) || null
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Pick an account…" />
                    )}
                    ListboxProps={{ style: { maxHeight: 200 } }}
                    disabled={!!id || isPosted}
                  />
                </div>
                <div>
                  <label className="rfpg-flab rfpg-flab-req">Date</label>
                  <input
                    className="rfpg-finput rfpg-finput-mono"
                    type="date"
                    disabled={isPosted}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>
            </section>

            {/* Denomination counting table (shared component) */}
            <section className="rfpg-card">
              <DenominationCounter
                lines={denominations}
                onQuantityChange={handleDenominationChange}
                currency={currency}
                disabled={isPosted}
                totalValue={actualCash}
              />
            </section>

            {/* Note */}
            <section className="rfpg-card">
              <div className="rfpg-card-head">
                <span>Note</span>
                <span className="rfpg-card-hint">
                  Anything unusual about this count
                </span>
              </div>
              <textarea
                className="rfpg-ftextarea"
                placeholder="Optional — e.g. £20 note found under the till drawer…"
                disabled={isPosted}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </section>
          </div>

          {/* Right rail: live calculations */}
          <aside className="rfpg-rail" aria-label="Live calculations">
            <div className="rfpg-calc-row">
              <span className="rfpg-calc-lab">System balance</span>
              <span className="rfpg-calc-val">
                {formatMoney(systemBalance, currency)}
              </span>
              <span className="rfpg-calc-formula">
                from transactions as of the selected date
              </span>
            </div>
            <div className="rfpg-calc-row">
              <span className="rfpg-calc-lab">Counted cash</span>
              <span className="rfpg-calc-val">
                {formatMoney(actualCash, currency)}
              </span>
              <span className="rfpg-calc-formula">
                Σ (denomination × quantity)
              </span>
            </div>
            <div className="rfpg-calc-row is-feature">
              <span className="rfpg-calc-lab">Variance</span>
              <span className={`rfpg-calc-val ${varianceCls}`}>
                {formatMoney(variance, currency)}
              </span>
              <span className="rfpg-calc-formula">
                actual − system · +surplus / −deficit
              </span>
            </div>

            {!isPosted && (
              <button
                type="button"
                className="rfpg-btn rfpg-btn-primary"
                onClick={handleSave}
              >
                {id ? "Update Draft" : "Create Draft"}
              </button>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export default ReconciliationFormPage;
